import { motion } from 'motion/react'
import { RevealItem, ScrollReveal } from '#/components/common/ScrollReveal'
import { TextAnimate } from '#/components/common/TextAnimate'
import { HighlightText } from '#/components/common/HighlightText'
import headerIcon from '#/assets/header-icon.svg'
import {
  WHY_CHOOSE_US_CONTENT,
  WHY_CHOOSE_US_REASONS,
} from '#/data/aboutWhyChooseUs'
import { LucideCheckCircle2 } from 'lucide-react'

/* ── Arabesque corner accent ─────────────────────────────────────────── */
function ArabesqueAccent({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 200 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {[0, 45, 90, 135].map((deg) => (
        <rect
          key={deg}
          x="50"
          y="50"
          width="100"
          height="100"
          rx="4"
          stroke="currentColor"
          strokeWidth="1"
          fill="none"
          transform={`rotate(${deg} 100 100)`}
        />
      ))}
      <circle
        cx="100"
        cy="100"
        r="40"
        stroke="currentColor"
        strokeWidth="1"
        fill="none"
      />
      <circle
        cx="100"
        cy="100"
        r="20"
        stroke="currentColor"
        strokeWidth="1"
        fill="none"
      />
    </svg>
  )
}

/* ── Component ───────────────────────────────────────────────────────── */
export default function WhyChooseUsSection() {
  return (
    <section
      className="relative w-full py-20 sm:py-28 bg-primary-50 overflow-hidden"
      id="why-choose-us"
    >
      {/* Subtle background gradients */}
      <div
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            'radial-gradient(60% 50% at 90% 10%, rgba(255,182,6,0.06) 0%, transparent 70%), radial-gradient(50% 60% at 5% 90%, rgba(0,77,64,0.06) 0%, transparent 70%)',
        }}
      />

      {/* Top-right arabesque */}
      <ArabesqueAccent className="pointer-events-none absolute right-0 top-0 h-56 w-56 text-primary-500 opacity-[0.07]" />
      {/* Bottom-left arabesque */}
      <ArabesqueAccent className="pointer-events-none absolute bottom-0 left-0 h-56 w-56 text-secondary-500 opacity-[0.06]" />

      <div className="container mx-auto flex flex-col gap-16">
        {/* Header */}
        <ScrollReveal className="flex flex-col items-center justify-center gap-4 text-center">
          <RevealItem>
            <img alt="" src={headerIcon} />
          </RevealItem>
          <RevealItem>
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-primary-700">
              {WHY_CHOOSE_US_CONTENT.eyebrow}
            </p>
          </RevealItem>
          <RevealItem className="w-full">
            <h2 className="text-4xl font-bold text-neutral-900 sm:text-5xl">
              {WHY_CHOOSE_US_CONTENT.title}{' '}
              <HighlightText
                inView
                text={WHY_CHOOSE_US_CONTENT.highlight}
                className="bg-linear-to-r from-primary-100/70 to-primary-300/70 text-neutral-900"
              />
            </h2>
          </RevealItem>
          <RevealItem className="max-w-2xl">
            <TextAnimate
              by="word"
              animation="fadeIn"
              duration={1.1}
              className="text-base text-neutral-500 sm:text-lg"
            >
              {WHY_CHOOSE_US_CONTENT.description}
            </TextAnimate>
          </RevealItem>
        </ScrollReveal>

        {/* Reason cards — 2-col alternating, same pattern as CourseWhySection */}
        <ScrollReveal
          className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
          stagger={0.12}
        >
          {WHY_CHOOSE_US_REASONS.map((reason, idx) => {
            const isEven = idx % 2 === 0
            return (
              <RevealItem key={reason.title}>
                <motion.div
                  whileHover={{ y: -6, transition: { duration: 0.25 } }}
                  className={`group relative flex h-full flex-col gap-5 rounded-2xl p-8 transition-shadow duration-300 hover:shadow-xl ${
                    isEven
                      ? 'bg-secondary-500 text-white'
                      : 'border border-neutral-200 bg-white'
                  }`}
                >
                  {/* Ghost number badge */}
                  <span
                    className={`pointer-events-none absolute right-6 top-4 text-7xl font-bold leading-none select-none ${
                      isEven ? 'text-white/8' : 'text-neutral-900/5'
                    }`}
                  >
                    {String(idx + 1).padStart(2, '0')}
                  </span>

                  {/* Icon */}
                  <div
                    className={`flex size-14 items-center justify-center rounded-xl ${
                      isEven ? 'bg-white/10' : 'bg-secondary-50'
                    }`}
                  >
                    <reason.icon
                      className={`text-2xl ${isEven ? 'text-primary-300' : 'text-secondary-500'}`}
                    />
                  </div>

                  {/* Check */}
                  <LucideCheckCircle2
                    className={`text-xl ${isEven ? 'text-primary-400' : 'text-secondary-500'}`}
                  />

                  {/* Text */}
                  <div className="flex flex-col gap-2">
                    <h3
                      className={`text-xl font-bold ${isEven ? 'text-white' : 'text-neutral-900'}`}
                    >
                      {reason.title}
                    </h3>
                    <p
                      className={`text-base leading-relaxed ${isEven ? 'text-neutral-300' : 'text-neutral-500'}`}
                    >
                      {reason.description}
                    </p>
                  </div>
                </motion.div>
              </RevealItem>
            )
          })}
        </ScrollReveal>
      </div>
    </section>
  )
}
